/**
 * App-aware copy for the add-in surfaces.
 *
 * The same /chat bundle runs inside PowerPoint, Excel, and Word. `app` (from
 * useOffice().app, i.e. the per-session clientApp) selects the right product
 * noun, tagline, and starter prompts so the sign-in screen, empty state, and
 * welcome cards read correctly for wherever the user actually launched Restack,
 * instead of always saying "PowerPoint" / "slides" / "deck".
 *
 * Defaults to PowerPoint for the plain web shell and any unknown host.
 */

const APP_COPY = {
  powerpoint: {
    appName: 'PowerPoint',
    pluginLabel: 'PowerPoint Plugin',
    // Empty-state subtitle under "Sign in to Restack".
    tagline: 'AI that builds your slides, inside PowerPoint',
    // Heading above the welcome/starter cards.
    welcomeHeading: 'What do you want to do with this presentation?',
    welcomeDescription: 'Choose a workflow or describe the outcome in your own words.',
    composerPlaceholder: 'Edit this presentation',
    // Welcome-card starters. Tapping fills the composer (does not auto-send).
    // Fallback workflow starters used while the host catalog is loading.
    starters: [
      { id: 'competitive-analysis', title: 'Competitive Analysis', prompt: '/competitive-analysis ' },
      { id: 'build-waterfall', title: 'Build Waterfall', prompt: '/build-waterfall ' },
      { id: 'lp-report', title: 'LP Report', prompt: '/lp-report ' },
      { id: 'ic-memo-deck', title: 'IC Memo Deck', prompt: '/ic-memo-deck ' },
    ],
  },
  excel: {
    appName: 'Excel',
    pluginLabel: 'Excel Plugin',
    tagline: 'AI that builds your models, inside Excel',
    welcomeHeading: 'What do you want to do with this workbook?',
    welcomeDescription: 'Choose a workflow or describe the outcome in your own words.',
    composerPlaceholder: 'Edit this workbook',
    starters: [
      { id: 'three-statement-model', title: 'Three-Statement Model', prompt: '/three-statement-model ' },
      { id: 'dcf-valuation', title: 'DCF Valuation', prompt: '/dcf-valuation ' },
      { id: 'lbo-model', title: 'LBO Model', prompt: '/lbo-model ' },
      { id: 'model-audit', title: 'Model Audit', prompt: '/model-audit ' },
    ],
  },
  word: {
    appName: 'Word',
    pluginLabel: 'Word Plugin',
    tagline: 'AI that drafts your documents, inside Word',
    welcomeHeading: 'What do you want to do with this document?',
    welcomeDescription: 'Choose a workflow or describe the outcome in your own words.',
    composerPlaceholder: 'Edit this document',
    starters: [
      { id: 'investment-committee-memo', title: 'Investment Committee Memo', prompt: '/investment-committee-memo ' },
      { id: 'due-diligence-report', title: 'Due Diligence Report', prompt: '/due-diligence-report ' },
      { id: 'board-paper', title: 'Board Paper', prompt: '/board-paper ' },
      { id: 'executive-summary', title: 'Executive Summary', prompt: '/executive-summary ' },
    ],
  },
};

/**
 * App-aware copy bundle for the given host. Falls back to PowerPoint for the
 * web shell or any unknown/null app value.
 * @param {string|null|undefined} app - 'powerpoint' | 'excel' | 'word'
 */
export function getAppCopy(app) {
  return APP_COPY[app] || APP_COPY.powerpoint;
}

/** Just the display name ("PowerPoint" | "Excel" | "Word"), Office for unknown pre-ready state. */
export function getAppName(app, fallback = 'Office') {
  return APP_COPY[app] ? APP_COPY[app].appName : fallback;
}
